import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Receipt, Clock, ArrowRight, Tag } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { blogPosts, getCategoryColor } from '../data/blogData';

const Blog = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Blog — Invoice & Tax Tips for Freelancers | FinScan</title>
        <meta name="description" content="Tips and guides for freelancers and independent contractors. Learn how to track expenses, create invoices, and save money at tax time." />
        <link rel="canonical" href="https://finscan.store/blog" />
        <meta property="og:title" content="FinScan Blog — Tips for Freelancers & Independent Contractors" />
        <meta property="og:description" content="Tips and guides for freelancers and independent contractors." />
        <meta property="og:url" content="https://finscan.store/blog" />
      </Helmet>

      <div className="min-h-screen bg-[#030712]">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/40 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center space-x-2">
                <Receipt className="w-8 h-8 text-indigo-400" />
                <span className="text-2xl font-bold font-['Outfit'] bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400">
                  FinScan
                </span>
              </Link>
              <div className="flex items-center space-x-4">
                <Link to="/electricians" className="text-muted-foreground hover:text-white transition-colors hidden sm:block">
                  For Contractors
                </Link>
                <Link to="/auth" className="bg-white text-black hover:bg-white/90 font-medium px-4 py-2 rounded-full transition-all">
                  Sign In
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Header */}
        <section className="pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-4xl md:text-5xl font-bold font-['Outfit'] tracking-tight mb-4">
                FinScan Blog
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Tips for Freelancers & Independent Contractors
              </p>
            </motion.div>
          </div>
        </section>

        {/* Blog Grid */}
        <section className="pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {blogPosts.map((post, i) => (
                <motion.article
                  key={post.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 transition-colors group"
                >
                  <Link to={`/blog/${post.slug}`}>
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getCategoryColor(post.category)}`}>
                          {post.category}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {post.readTime}
                        </span>
                      </div>
                      
                      <h2 className="text-xl font-semibold mb-3 group-hover:text-indigo-400 transition-colors">
                        {post.title}
                      </h2>
                      
                      <p className="text-muted-foreground mb-4 line-clamp-2">
                        {post.excerpt}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">{post.date}</span>
                        <span className="flex items-center gap-1 text-indigo-400 text-sm font-medium group-hover:gap-2 transition-all">
                          Read more <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 border-t border-white/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to simplify your finances?</h2>
            <p className="text-muted-foreground mb-6">
              Join thousands of freelancers and contractors who track smarter with FinScan.
            </p>
            <Link
              to="/auth"
              className="inline-block bg-indigo-500 text-white hover:bg-indigo-400 font-medium px-8 py-3 rounded-full transition-all"
            >
              Start Free Today
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-muted-foreground text-sm">
                © 2026 FinScan. Built for independent workers.
              </p>
              <div className="flex gap-6 text-sm">
                <Link to="/electricians" className="text-muted-foreground hover:text-white transition-colors">Contractors</Link>
                <Link to="/1099" className="text-muted-foreground hover:text-white transition-colors">1099 Workers</Link>
                <Link to="/freelancers" className="text-muted-foreground hover:text-white transition-colors">Freelancers</Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Blog;
